import React from 'react';
import _ from 'lodash';

import './RydFretboard.css';
import { Note } from '../../model/core';
import { normalizeClassName } from '../../render/renderUtils';

export type Tuning = number[];

export interface FretboardConfiguration<T extends readonly number[]> {
    numberOfStrings: number;
    numberOfFrets: number;
    tuning: Tuning;
}

type Predicate = (stringIndex: number, fret: number, baseNote: Note) => boolean;
export type HighlightPredicate = (stringIndex: number, fret: number, baseNote: Note) => string[];

const isC: Predicate = (stringIndex, fret, baseNote) => baseNote.getTone().up(fret).getName() === "C";
export const activeIfC: HighlightPredicate = (stringIndex, fret, baseNote) => {
    if (isC(stringIndex, fret, baseNote)) {
        return ["fretboard--cell-active"];
    }
    return [];
}

function computeFretPosition(config: FretboardConfiguration<any>): { left: number, width: number }[] {
    return _.range(config.numberOfFrets + 1).map((fret) => {
        const v = 2 ** (fret / 12);
        const p = 1 - v;
        return {
            left: 1 / v,
            width: 1 / v - 1 / (2 ** ((fret + 1) / 12)),
        }
    });
}

function FretboardCell({
    stringIndex,
    fret,
    baseNote,
    fretPositions,
    fretboardConfig,
    highlightClasses,
    stylingSource = (n: Note) => []
}: {
    stringIndex: number,
    fret: number,
    baseNote: Note,
    fretPositions: { left: number, width: number }[],
    fretboardConfig: FretboardConfiguration<any>,
    highlightClasses: string[],
    stylingSource?: (n: Note) => string[],
}) {
    const note = Note.fromIndex(baseNote.index + fret);
    const classes = [
        "fretboard--cell",
        fret === 0 ? "" : "strike-line"
    ]
        .concat(highlightClasses)
        .concat(stylingSource(note));

    return (
        <div
            className={normalizeClassName(classes)}
            key={`${stringIndex}-${fret}`}
            style={{
                left: `${fretPositions[fret].left * 100}%`,
                width: `${fretPositions[fret].width * 100}%`,
            }}>
            {note.getName()}
        </div>
    );
}

function RydFretboard({
    config,
    highlightPredicates = [],
    stylingSource = (n: Note) => []
}: {
    config: FretboardConfiguration<any>,
    highlightPredicates?: HighlightPredicate[],
    stylingSource?: (n: Note) => string[],
}) {

    const fretPositions = computeFretPosition(config).map((pos) => ({
        left: pos.left * 4/3,
        width: pos.width * 4/3
    }));

    return (

        <div className='ryd-fretboard'>
            <div className='fretboard--fret-head'>
                {_.range(config.numberOfFrets + 1)
                    .map((fret) =>
                        <div className="fretboard--fret-head-cell"
                            key={fret}
                            style={{
                                left: `${fretPositions[fret].left * 100}%`,
                                width: `${fretPositions[fret].width * 100}%`,
                            }}>
                            {fret}
                        </div>
                    )}
            </div>
            {_.rangeRight(config.numberOfStrings)
                .map((stringIndex) => {
                    const baseNote = Note.fromIndex(config.tuning[stringIndex] || 0);
                    return (
                        <div key={stringIndex}
                            className={"fretboard--string"}>
                            {_.range(config.numberOfFrets + 1).map((fret) => (
                                <FretboardCell
                                    key={`${stringIndex}-${fret}`}
                                    stringIndex={stringIndex}
                                    fret={fret}
                                    baseNote={baseNote}
                                    fretPositions={fretPositions}
                                    fretboardConfig={config}
                                    stylingSource={stylingSource}
                                    highlightClasses={highlightPredicates.map(predicate => predicate(stringIndex, fret, baseNote)).flat()}
                                />
                            ))}
                        </div>
                    );
                })}
        </div>

    );
}

export {
    RydFretboard,
    FretboardCell
}